import React, { useRef, useState } from "react";
import ReactTooltip from "react-tooltip";
import styled from "styled-components/macro";
import { TextMdRegular, TextSmRegular } from "../Typography/TypographyV2";

let idCounter = 0;
const useUniqueId = () => {
  const id = useRef<number | null>(null);
  if (id.current === null) {
    id.current = ++idCounter;
  }
  return String(id.current);
};

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

const EllipsisText = styled.span`
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
`;

type OverflowTextProps = {
  children: React.ReactNode;
  tooltipId: string;
  as: typeof TextMdRegular | typeof TextSmRegular;
  color?: "primary" | "tertiary";
};

const OverflowText = ({
  children,
  tooltipId,
  as: Tag,
  color,
}: OverflowTextProps) => {
  const ref = useRef<HTMLSpanElement>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);

  const checkOverflow = () => {
    const el = ref.current;
    if (el) {
      setIsOverflowing(el.scrollWidth > el.clientWidth);
    }
  };

  const content = typeof children === "string" ? children : undefined;

  return (
    <>
      <Tag
        color={color}
        data-tip={isOverflowing ? content : undefined}
        data-for={isOverflowing ? tooltipId : undefined}
        onMouseEnter={checkOverflow}
      >
        <EllipsisText ref={ref}>{children}</EllipsisText>
      </Tag>
      {isOverflowing && (
        <ReactTooltip id={tooltipId} effect="solid" place="top" />
      )}
    </>
  );
};

export type TableCellTwoLineProps = {
  primaryText: React.ReactNode;
  secondaryText?: React.ReactNode;
};

export const TableCellTwoLine = ({
  primaryText,
  secondaryText,
}: TableCellTwoLineProps) => {
  const uid = useUniqueId();
  const primaryTooltipId = `two-line-primary-${uid}`;
  const secondaryTooltipId = `two-line-secondary-${uid}`;

  return (
    <Wrapper>
      <OverflowText as={TextMdRegular} tooltipId={primaryTooltipId}>
        {primaryText}
      </OverflowText>
      {!!secondaryText && (
        <OverflowText
          as={TextSmRegular}
          color="tertiary"
          tooltipId={secondaryTooltipId}
        >
          {secondaryText}
        </OverflowText>
      )}
    </Wrapper>
  );
};
